import * as React from 'react'

import { IWorkflowStep, PermitFieldType } from '~/client/graph'
import { ListSelector } from '~/client/src/shared/components/ListSelector/ListSelector'
import { IListSelectorItem } from '~/client/src/shared/components/ListSelector/types'
import PermitFieldIcon from '~/client/src/shared/components/PermitFieldIcon/PermitFieldIcon'
import { userPermitFieldTypes } from '~/client/src/shared/constants/PermitFieldTypeConstants'
import { getWorkflowStepDisplayName } from '~/client/src/shared/localization/enumDisplayTexts'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { getWorkflowFieldTypeLabel } from '~/client/src/shared/utils/WorkflowFieldTypeHelpers'

interface IFieldItem extends IListSelectorItem {
  id: string
  fieldType: PermitFieldType
  fieldCaption: string
  stepNumber: number
  fieldIndex: number
  stepId: string
}

interface IStepInfo {
  step: IWorkflowStep
  stepNumber: number
  userFields: IWorkflowStep['fields']
}

interface IProps {
  allWorkflowSteps: IWorkflowStep[]
  selectedFieldIds: Set<string>
  allowedFieldTypes?: PermitFieldType[]
  onFieldToggle: (fieldId: string, isSelected: boolean) => void
  onFieldsToggle?: (fieldIds: string[], isSelected: boolean) => void
}

export default function ByWorkflowUserPicker({
  allWorkflowSteps,
  selectedFieldIds,
  allowedFieldTypes = userPermitFieldTypes,
  onFieldToggle,
  onFieldsToggle,
}: IProps) {
  const stepsWithFields = React.useMemo((): IStepInfo[] => {
    const result: IStepInfo[] = []
    let stepNumber = 0

    allWorkflowSteps.forEach(step => {
      const userFields = step.fields?.filter(field =>
        allowedFieldTypes.includes(field.type),
      )
      if (!userFields?.length) return
      stepNumber++
      result.push({ step, stepNumber, userFields })
    })

    return result
  }, [allWorkflowSteps, allowedFieldTypes])

  const fieldItems = React.useMemo(
    (): IFieldItem[] =>
      stepsWithFields.flatMap(({ step, stepNumber, userFields }) =>
        userFields.map((field, index) => ({
          id: field.id,
          fieldType: field.type,
          fieldCaption: field.caption || getWorkflowFieldTypeLabel(field.type),
          stepNumber,
          fieldIndex: index + 1,
          stepId: step.id,
        })),
      ),
    [stepsWithFields],
  )

  const groupingConfig = React.useMemo(
    () => ({
      options: [{ key: 'step', label: Localization.translator.step }],
      defaultKey: 'step',
      buildGroupConfig: () => ({
        groupBy: (item: IFieldItem) => item.stepId,
        getGroupLabel: (stepId: string) => {
          const info = stepsWithFields.find(s => s.step.id === stepId)
          return info
            ? getWorkflowStepDisplayName(info.step.type, info.stepNumber - 1)
            : stepId
        },
      }),
    }),
    [stepsWithFields],
  )

  const handleItemToggle = React.useCallback(
    (id: string) => {
      onFieldToggle(id, !selectedFieldIds.has(id))
    },
    [selectedFieldIds, onFieldToggle],
  )

  const handleToggleAll = React.useCallback(
    (ids: string[], selectAll: boolean) => {
      onFieldsToggle?.(ids, selectAll)
    },
    [onFieldsToggle],
  )

  const renderFieldRow = React.useCallback(
    (item: IFieldItem) => (
      <>
        <span className="tw:min-w-8 tw:shrink-0 tw:text-right tw:text-body-sm tw:text-muted-foreground">
          {item.stepNumber}.{item.fieldIndex}
        </span>
        <PermitFieldIcon fieldName={item.fieldType} />
        <span className="tw:truncate">{item.fieldCaption}</span>
      </>
    ),
    [],
  )

  return (
    <ListSelector.Root<IFieldItem>
      items={fieldItems}
      selectedIds={selectedFieldIds}
      onItemToggle={handleItemToggle}
      onToggleAll={onFieldsToggle ? handleToggleAll : undefined}
      grouping={groupingConfig}
      className="tw:flex-1 tw:min-h-0"
    >
      <ListSelector.Counter
        formatTotal={Localization.translator.xFieldsCount}
      />
      <ListSelector.List renderItemContent={renderFieldRow} />
    </ListSelector.Root>
  )
}
